import React, { useState } from "react";
import { Trophy, LogIn, UserPlus, KeyRound, ArrowLeft } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const SECURITY_QUESTIONS = [
  "Qual è il nome del tuo primo animale domestico?",
  "In quale città sei nato?",
  "Qual è il tuo circolo di padel preferito?",
  "Come si chiamava la tua scuola elementare?",
  "Qual è il tuo compagno di coppia preferito?",
];
const CUSTOM = "__custom__";

export const AuthScreen = () => {
  const { login, register, resetPassword } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | reset
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [questionChoice, setQuestionChoice] = useState(SECURITY_QUESTIONS[0]);
  const [customQuestion, setCustomQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // reset flow
  const [resetStep, setResetStep] = useState(1);
  const [resetQuestion, setResetQuestion] = useState("");
  const [resetAnswer, setResetAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const errorText = (err, fallback = "Qualcosa è andato storto. Riprova.") => {
    const d = err?.response?.data?.detail;
    return typeof d === "string" ? d : fallback;
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setResetStep(1);
    setResetQuestion("");
    setResetAnswer("");
    setNewPassword("");
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(username.trim(), password);
      } else {
        const q = questionChoice === CUSTOM ? customQuestion.trim() : questionChoice;
        await register(username.trim(), password, q, answer.trim());
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const fetchQuestion = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { security_question } = await api.resetQuestion(username.trim());
      setResetQuestion(security_question);
      setResetStep(2);
    } catch (err) {
      setError(errorText(err, "Utente non trovato o senza domanda di sicurezza."));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await resetPassword(username.trim(), resetAnswer.trim(), newPassword);
    } catch (err) {
      setError(errorText(err, "Risposta di sicurezza errata."));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "bg-[#121b2d] border-slate-700 text-slate-100 h-12";
  const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-400";

  return (
    <div className="min-h-screen bg-[#090d16] text-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#ccff00] flex items-center justify-center mb-4">
            <Trophy className="w-8 h-8 text-[#090d16]" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Padel all'improvviso</h1>
          <p className="text-slate-400 text-sm mt-1">Organizza Incontri di Padel</p>
        </div>

        {mode === "reset" ? (
          <div data-testid="reset-panel">
            <button
              onClick={() => switchMode("login")}
              data-testid="reset-back-btn"
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-4"
            >
              <ArrowLeft className="w-4 h-4" /> Torna all'accesso
            </button>
            <div className="flex items-center gap-2 mb-5">
              <KeyRound className="w-5 h-5 text-[#ccff00]" />
              <h2 className="font-heading font-bold text-lg">Recupera password</h2>
            </div>

            {resetStep === 1 ? (
              <form onSubmit={fetchQuestion} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className={labelCls}>Nome utente</Label>
                  <Input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Il tuo nome utente"
                    data-testid="reset-username-input"
                    className={inputCls}
                  />
                </div>
                {error && <p className="text-sm text-[#ff4d4d]" data-testid="auth-error">{error}</p>}
                <Button
                  type="submit"
                  disabled={busy || !username.trim()}
                  data-testid="reset-fetch-question-btn"
                  className="w-full bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl h-12 disabled:opacity-40"
                >
                  {busy ? "Attendi..." : "Continua"}
                </Button>
              </form>
            ) : (
              <form onSubmit={submitReset} className="space-y-4">
                <div className="rounded-xl bg-[#121b2d] border border-[#1e2c4a] px-3 py-3">
                  <p className="text-xs text-slate-400 mb-1">Domanda di sicurezza</p>
                  <p className="text-sm font-semibold text-slate-100" data-testid="reset-question-text">{resetQuestion}</p>
                </div>
                <div className="space-y-1.5">
                  <Label className={labelCls}>Risposta</Label>
                  <Input
                    value={resetAnswer}
                    onChange={(e) => setResetAnswer(e.target.value)}
                    placeholder="La tua risposta segreta"
                    data-testid="reset-answer-input"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className={labelCls}>Nuova password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••"
                    data-testid="reset-new-password-input"
                    className={inputCls}
                  />
                </div>
                {error && <p className="text-sm text-[#ff4d4d]" data-testid="auth-error">{error}</p>}
                <Button
                  type="submit"
                  disabled={busy || !resetAnswer.trim() || !newPassword}
                  data-testid="reset-submit-btn"
                  className="w-full bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl h-12 disabled:opacity-40"
                >
                  {busy ? "Attendi..." : "Reimposta password"}
                </Button>
              </form>
            )}
          </div>
        ) : (
          <>
            <div className="flex gap-2 bg-[#121b2d] border border-[#1e2c4a] rounded-full p-1 mb-6">
              <button
                onClick={() => switchMode("login")}
                data-testid="tab-login"
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "login" ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300"}`}
              >
                Accedi
              </button>
              <button
                onClick={() => switchMode("register")}
                data-testid="tab-register"
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "register" ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300"}`}
              >
                Registrati
              </button>
            </div>

            <form onSubmit={submitAuth} className="space-y-4">
              <div className="space-y-1.5">
                <Label className={labelCls}>Nome utente</Label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Es. Marco"
                  data-testid="auth-username-input"
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <Label className={labelCls}>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••"
                  data-testid="auth-password-input"
                  className={inputCls}
                />
              </div>

              {mode === "register" && (
                <>
                  <div className="space-y-1.5">
                    <Label className={labelCls}>Domanda di sicurezza</Label>
                    <Select value={questionChoice} onValueChange={setQuestionChoice}>
                      <SelectTrigger data-testid="auth-security-question-select" className="bg-[#121b2d] border-slate-700 text-slate-100 h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
                        {SECURITY_QUESTIONS.map((q) => (
                          <SelectItem key={q} value={q}>{q}</SelectItem>
                        ))}
                        <SelectItem value={CUSTOM}>Altra domanda (personalizzata)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {questionChoice === CUSTOM && (
                    <div className="space-y-1.5">
                      <Label className={labelCls}>La tua domanda</Label>
                      <Input
                        value={customQuestion}
                        onChange={(e) => setCustomQuestion(e.target.value)}
                        placeholder="Scrivi la tua domanda segreta"
                        data-testid="auth-custom-question-input"
                        className={inputCls}
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className={labelCls}>Risposta segreta</Label>
                    <Input
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder="Servirà per recuperare l'accesso"
                      data-testid="auth-security-answer-input"
                      className={inputCls}
                    />
                  </div>
                </>
              )}

              {error && <p className="text-sm text-[#ff4d4d]" data-testid="auth-error">{error}</p>}

              <Button
                type="submit"
                disabled={busy || !username.trim() || !password || (mode === "register" && (!answer.trim() || (questionChoice === CUSTOM && !customQuestion.trim())))}
                data-testid="auth-submit-btn"
                className="w-full bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl h-12 disabled:opacity-40"
              >
                {mode === "login" ? <LogIn className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                {busy ? "Attendi..." : mode === "login" ? "Accedi" : "Crea account"}
              </Button>
            </form>

            {mode === "login" && (
              <button
                onClick={() => switchMode("reset")}
                data-testid="forgot-password-btn"
                className="w-full text-center text-xs text-[#ccff00]/80 hover:text-[#ccff00] mt-4"
              >
                Password dimenticata?
              </button>
            )}

            <p className="text-center text-xs text-slate-500 mt-4">
              {mode === "login" ? "Non hai un account? Tocca Registrati." : "Hai già un account? Tocca Accedi."}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
